// Tela: Dashboard (início).
// Extraído de App.js sem alteração de comportamento.
import { useState, useEffect, useRef } from "react";
import React from "react";
import ReactDOM from "react-dom";
import { loadLS, saveLS } from "../lib/storage";
import { sb } from "../lib/supabase";
import { hexToRgb, useIsMobile, useTheme } from "../lib/theme";
import { MedalBadge, NavIcon } from "../components/icons";
import { ADMIN_EMAILS, FRASES_DO_DIA, findContest, CAL_SEED } from "../lib/constants";
import { TacCorners, TacEyebrow, TickBar, ContestEmblem } from "../components/ui";
import { calEmpty, calFmtLong } from "../lib/calendar";
import { localDayKey } from "../lib/dates";
import { STREAK_MIN_SEC, useStudyDays, computeStreak, badgeExtra, useMyRankOverride } from "../lib/stats";
import { BadgesGrid } from "../components/badges";
import { UserProfileModal } from "../components/profile";

// ── Resumo de um dia (clique no quadradinho da sequência) ───────────────────────
function DaySummary({ dayKey, sessions, byDay, uid, isAdmin, onClose }) {
  const t = useTheme();
  const [cal, setCal] = useState(null);                 // dados do Calendário (matérias e anotações)
  useEffect(() => {
    let alive = true;
    (async () => {
      let d = null;
      try { const { data: row } = await sb.from("study_calendar").select("data").eq("user_id", uid).maybeSingle(); d = row && row.data; } catch {}
      if (!d || !d.days) d = loadLS("g17_study_calendar_" + uid, null) || (isAdmin ? CAL_SEED : calEmpty());
      if (alive) setCal(d);
    })();
    return () => { alive = false; };
  }, [uid, isAdmin]);
  useEffect(() => {
    const onKey = e => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey); return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const date = new Date(dayKey + "T12:00:00");
  const sec = byDay[dayKey] || 0;               // conta para a sequência (inclui o Calendário, se for o caso)
  const list = [...(sessions[dayKey] || [])].sort((a, b) => new Date(a.at) - new Date(b.at));
  const longest = list.reduce((m, x) => Math.max(m, x.sec), 0);
  const first = list[0], last = list[list.length - 1];
  const hm = iso => new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  const dur = sc => { const m = Math.round(sc / 60); return m >= 60 ? `${Math.floor(m / 60)}h${String(m % 60).padStart(2, "0")}` : sc > 0 && m === 0 ? "<1 min" : `${m} min`; };
  const subj = cal ? Object.entries((cal.days || {})[dayKey] || {}).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]) : [];
  const calTotal = subj.reduce((a, [, v]) => a + v, 0);
  const notes = cal ? ((cal.notes || {})[dayKey] || []) : [];
  const todayK = localDayKey(new Date());
  const counts = sec >= STREAK_MIN_SEC;
  const badge = counts ? { txt: "Conta na sequência", c: "#4ade80" }
    : sec > 0 ? { txt: dayKey === todayK ? `Faltam ${Math.ceil((STREAK_MIN_SEC - sec) / 60)} min para contar` : "Abaixo de 10 min", c: "#fbbf24" }
    : { txt: dayKey === todayK ? "Ainda sem estudo hoje" : "Sem estudo no cronômetro", c: t.textFaint };
  const card = { background: t.bgInner, border: `1px solid ${t.borderIn}`, borderRadius: 10, padding: "0.65rem 0.8rem" };
  const lab = { fontSize: "0.66rem", color: t.textDim, textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 700, margin: "0 0 0.4rem" };
  return ReactDOM.createPortal(
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 9990, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
      <div onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={`Resumo de ${date.toLocaleDateString("pt-BR")}`}
        style={{ width: "100%", maxWidth: 480, maxHeight: "88vh", display: "flex", flexDirection: "column", background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 16, boxShadow: "0 24px 64px rgba(0,0,0,0.5)", fontFamily: t.fontBody }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "0.8rem", padding: "1rem 1.2rem", borderBottom: `1px solid ${t.borderIn}` }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: "0.68rem", color: t.textDim, textTransform: "uppercase", letterSpacing: "0.08em" }}>Resumo do dia</div>
            <div style={{ fontSize: "1.05rem", fontWeight: 800, color: t.text }}>{(l => l.charAt(0).toUpperCase() + l.slice(1))(date.toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long", year: "numeric" }))}</div>
            <div style={{ marginTop: "0.35rem", display: "inline-flex", alignItems: "center", gap: "0.35rem", fontSize: "0.72rem", fontWeight: 700, color: badge.c }}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: badge.c }} />{badge.txt}
            </div>
          </div>
          <button onClick={onClose} aria-label="Fechar" style={{ width: 34, height: 34, flexShrink: 0, borderRadius: 9, border: `1px solid ${t.border}`, background: "transparent", color: t.textDim, cursor: "pointer", fontSize: "1.1rem", lineHeight: 1 }}>×</button>
        </div>
        <div style={{ overflowY: "auto", padding: "0.9rem 1.2rem 1.1rem", display: "flex", flexDirection: "column", gap: "0.9rem" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "0.5rem" }}>
            {[["Tempo no cronômetro", list.length ? dur(list.reduce((a, x) => a + x.sec, 0)) : "—"], ["Sessões", list.length || "—"], ["Maior sessão", longest ? dur(longest) : "—"]].map(([l, v]) => (
              <div key={l} style={{ ...card, textAlign: "center", padding: "0.6rem 0.4rem" }}>
                <div className="tac-num" style={{ fontSize: "1.15rem", fontWeight: 800, color: t.text }}>{v}</div>
                <div style={{ fontSize: "0.62rem", color: t.textDim, marginTop: "0.15rem", lineHeight: 1.25 }}>{l}</div>
              </div>
            ))}
          </div>
          {first && <div style={{ fontSize: "0.78rem", color: t.textMid }}>Primeiro registro às <strong style={{ color: t.text }}>{hm(first.at)}</strong>{list.length > 1 && <> · último às <strong style={{ color: t.text }}>{hm(last.at)}</strong></>}</div>}

          <div>
            <p style={lab}>Por matéria {calTotal > 0 && <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 500 }}>· {calFmtLong(calTotal)} no Calendário</span>}</p>
            {!cal ? <div style={{ fontSize: "0.8rem", color: t.textFaint }}>Carregando…</div>
              : subj.length === 0 ? <div style={{ fontSize: "0.8rem", color: t.textFaint }}>Nenhuma matéria registrada neste dia no Calendário.</div>
              : <div style={{ display: "flex", flexDirection: "column", gap: "0.45rem" }}>
                  {subj.map(([name, v]) => (
                    <div key={name}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: "0.6rem", fontSize: "0.8rem", color: t.text }}>
                        <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>{name}</span>
                        <span className="tac-num" style={{ color: t.textMid, flexShrink: 0 }}>{calFmtLong(v)}</span>
                      </div>
                      <div style={{ height: 5, borderRadius: 3, background: t.bgInner, marginTop: 3, overflow: "hidden" }}>
                        <div style={{ width: `${Math.max(4, (v / subj[0][1]) * 100)}%`, height: "100%", background: t.ac, opacity: 0.85, borderRadius: 3 }} />
                      </div>
                    </div>
                  ))}
                </div>}
          </div>

          {list.length > 0 && (
            <div>
              <p style={lab}>Sessões do cronômetro</p>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}>
                {list.map((x, i) => (
                  <div key={i} style={{ ...card, display: "flex", justifyContent: "space-between", fontSize: "0.8rem", color: t.text, padding: "0.45rem 0.8rem" }}>
                    <span className="tac-num" style={{ color: t.textMid }}>{hm(x.at)}</span><span className="tac-num" style={{ fontWeight: 700 }}>{dur(x.sec)}</span>
                  </div>
                ))}
              </div>
              <p style={{ margin: "0.4rem 0 0", fontSize: "0.68rem", color: t.textFaint, lineHeight: 1.4 }}>O horário é o de quando a sessão foi salva.</p>
            </div>
          )}

          {notes.length > 0 && (
            <div>
              <p style={lab}>Anotações do dia</p>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
                {notes.map(n => <div key={n.id} style={{ ...card, fontSize: "0.82rem", color: t.text, whiteSpace: "pre-wrap", overflowWrap: "anywhere", lineHeight: 1.5 }}>{n.text}</div>)}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>, document.body);
}

function StudyStreak({ byDay, sessions = {}, cal, loaded, uid, isAdmin }) {
  const t = useTheme();
  const [openDay, setOpenDay] = useState(null);
  const [pieHover, setPieHover] = useState(null);   // { i, x, y }
  const cardRef = useRef(null);
  const [cardW, setCardW] = useState(0);
  useEffect(() => {
    const el = cardRef.current; if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([e]) => setCardW(e.contentRect.width)); ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const isMobile = useIsMobile();
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [showHelp, setShowHelp] = useState(() => loadLS("sf_streakHelp", false));
  const scrollRef = useRef(null);
  const { current, best, todayDone, todaySec } = computeStreak(byDay);
  const minYear = Math.min(thisYear, ...Object.keys(byDay).map(k => Number(k.slice(0, 4))).filter(Boolean));

  // grade: cada linha é um mês (jan…dez) e cada coluna é o dia do mês (1…31)
  const todayK = localDayKey(new Date());
  const MONTHS_SHORT = ["jan","fev","mar","abr","mai","jun","jul","ago","set","out","nov","dez"];
  const [boxW, setBoxW] = useState(0);
  useEffect(() => {
    const el = scrollRef.current; if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([e]) => setBoxW(e.contentRect.width)); ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const gap = 2;
  const labelW = isMobile ? 22 : 26;
  const cell = isMobile ? Math.max(7, Math.min(12, Math.floor((boxW - labelW - 4) / 31) - gap)) : (cardW && cardW < 1060 ? 9 : 11);
  const [rr, gg, bb] = hexToRgb(t.ac);
  const color = sec => sec >= STREAK_MIN_SEC ? t.ac : sec > 0 ? `rgba(${rr},${gg},${bb},0.22)` : t.bgInner;
  const fmt = sec => { const m = Math.round(sec / 60); return m >= 60 ? `${Math.floor(m / 60)}h${String(m % 60).padStart(2, "0")}` : `${m} min`; };


  const restMin = Math.max(0, Math.ceil((STREAK_MIN_SEC - todaySec) / 60));
  // Matérias estudadas no ano mostrado (minutos do Calendário, inclusive os totais mensais da planilha)
  const PIE_COLORS = ["#D08A4F", "#4C9AFF", "#3DD68C", "#B48CFF", "#F5C451", "#F472B6", "#3ECFD8"];
  const fmtH = sc => { const m = Math.round(sc / 60); return m >= 60 ? `${Math.floor(m / 60)}h${String(m % 60).padStart(2, "0")}` : `${m} min`; };
  const pie = (() => {
    const tot = {};
    const add = (name, min) => { if (min > 0) tot[name] = (tot[name] || 0) + min; };
    Object.entries((cal && cal.days) || {}).forEach(([k, subj]) => { if (k.startsWith(year + "-")) Object.entries(subj || {}).forEach(([n, m]) => add(n, Number(m) || 0)); });
    Object.entries((cal && cal.bulk) || {}).forEach(([k, subj]) => { if (k.startsWith(year + "-")) Object.entries(subj || {}).forEach(([n, m]) => add(n, Number(m) || 0)); });
    const all = Object.entries(tot).sort((a, b) => b[1] - a[1]);
    const head = all.slice(0, 7), rest = all.slice(7).reduce((a, [, v]) => a + v, 0);
    const items = head.map(([name, min], i) => ({ name, sec: min * 60, color: PIE_COLORS[i] }));
    if (rest > 0) items.push({ name: "Outras", sec: rest * 60, color: "#7b8494" });
    const total = items.reduce((a, x) => a + x.sec, 0);
    let acc = 0;
    items.forEach(x => { x.pct = total ? x.sec / total : 0; x.a0 = acc; acc += x.pct; x.a1 = acc; });
    return { items, total };
  })();
  const PS = isMobile ? 104 : 116, PR = PS / 2 - 3, PH = PR * 0.56;   // tamanho, raio externo e do furo
  const arcPath = (a0, a1, hot) => {
    const R = hot ? PR + 2.5 : PR, c = PS / 2;
    const pt = (a, r) => [c + r * Math.sin(a * 2 * Math.PI), c - r * Math.cos(a * 2 * Math.PI)];
    if (a1 - a0 >= 0.9999) { return `M ${c} ${c - R} A ${R} ${R} 0 1 1 ${c - 0.01} ${c - R} L ${c - 0.01} ${c - PH} A ${PH} ${PH} 0 1 0 ${c} ${c - PH} Z`; }
    const [x0, y0] = pt(a0, R), [x1, y1] = pt(a1, R), [x2, y2] = pt(a1, PH), [x3, y3] = pt(a0, PH);
    const big = a1 - a0 > 0.5 ? 1 : 0;
    return `M ${x0} ${y0} A ${R} ${R} 0 ${big} 1 ${x1} ${y1} L ${x2} ${y2} A ${PH} ${PH} 0 ${big} 0 ${x3} ${y3} Z`;
  };
  return (
    <div ref={cardRef} style={{ display:"flex", flexWrap:"wrap", gap:"1rem", alignItems:"stretch" }}>
    <div className="g17-hover-lift" style={{ flex:"1 1 700px", maxWidth:"100%", minWidth:0, boxSizing:"border-box", background:t.bgCard, border:`1px solid ${t.border}`, borderRadius:12, padding: isMobile ? "0.85rem" : "0.9rem 1.1rem", position:"relative", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <TacCorners color={t.ac} size={10} thickness={1.5} inset={6} opacity={0.35} />
      <div style={{ display:"flex", flexDirection: isMobile ? "column" : "row", gap: isMobile ? "0.8rem" : "2rem", alignItems: isMobile ? "stretch" : "center", justifyContent:"center", width:"100%" }}>
        <div style={{ order:1, flex: isMobile ? "none" : "0 0 230px", minWidth:0, display:"flex", flexDirection:"column", gap:"0.7rem" }}>
        <div style={{ display:"flex", alignItems:"center", gap:"0.7rem", minWidth:0 }}>
          <div style={{ width:42, height:42, borderRadius:12, background: current > 0 ? t.acDim : t.bgInner, border:`1px solid ${current > 0 ? t.acBorder : t.border}`, color: current > 0 ? t.ac : t.textFaint, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
            <NavIcon id="chama" size={22} />
          </div>
          <div style={{ minWidth:0 }}>
            <div style={{ fontSize: isMobile ? "1rem" : "1.05rem", fontWeight:800, color:t.text, lineHeight:1.2 }}>
              {!loaded ? "Carregando sequência…" : current === 0 ? "Comece sua sequência hoje" : <><span className="tac-num" style={{ color:t.ac }}>{current}</span> {current === 1 ? "dia" : "dias"} de sequência!</>}
            </div>
            <div style={{ fontSize:"0.76rem", color:t.textDim, marginTop:"0.15rem" }}>
              {todayDone ? <span style={{ color:"#4ade80" }}>Hoje já conta ✓</span> : <>Hoje: {fmt(todaySec)} de 10 min{restMin > 0 && current > 0 ? ` · faltam ${restMin} min para manter` : ""}</>}
              {best > 0 && <> · Recorde: <strong className="tac-num" style={{ color:t.textMid }}>{best} {best === 1 ? "dia" : "dias"}</strong></>}
            </div>
          </div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:"0.35rem" }}>
          <button onClick={() => setYear(y => y - 1)} disabled={year <= minYear} aria-label="Ano anterior"
            style={{ width:30, height:30, borderRadius:8, border:`1px solid ${t.border}`, background:"transparent", color: year <= minYear ? t.textGhost : t.textMid, cursor: year <= minYear ? "default" : "pointer", display:"flex", alignItems:"center", justifyContent:"center" }}><NavIcon id="esquerda" size={15} /></button>
          <span className="tac-num" style={{ fontSize:"0.85rem", fontWeight:700, color:t.text, minWidth:40, textAlign:"center" }}>{year}</span>
          <button onClick={() => setYear(y => y + 1)} disabled={year >= thisYear} aria-label="Próximo ano"
            style={{ width:30, height:30, borderRadius:8, border:`1px solid ${t.border}`, background:"transparent", color: year >= thisYear ? t.textGhost : t.textMid, cursor: year >= thisYear ? "default" : "pointer", display:"flex", alignItems:"center", justifyContent:"center" }}><NavIcon id="direita" size={15} /></button>
        </div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:"0.8rem", flexWrap:"wrap" }}>
        <div style={{ display:"flex", alignItems:"center", gap:"0.35rem", fontSize:"0.66rem", color:t.textFaint }}>
          <span>Menos</span>
          {[0, 60, STREAK_MIN_SEC].map(v => <span key={v} style={{ width:10, height:10, borderRadius:2, background:color(v), display:"inline-block" }} />)}
          <span>Mais</span>
        </div>
        <button onClick={() => { setShowHelp(v => { saveLS("sf_streakHelp", !v); return !v; }); }}
          style={{ background:"transparent", border:"none", padding:0, color:t.ac, fontSize:"0.74rem", cursor:"pointer", fontFamily:"inherit" }}>{showHelp ? "Ocultar explicação" : "Como funciona?"}</button>
      </div>
      {showHelp && (
        <p style={{ margin:0, fontSize:"0.78rem", lineHeight:1.55, color:t.textMid }}>
          A sequência conta os dias seguidos em que você estudou pelo menos <strong>10 minutos</strong> com o cronômetro do Ciclo. Se passar um dia inteiro sem chegar aos 10 minutos, ela recomeça do zero. Toque em um quadradinho para ver o resumo do dia. Mantenha a constância!
        </p>
      )}
        </div>
      <div ref={scrollRef} style={{ order:3, overflowX:"auto", WebkitOverflowScrolling:"touch", paddingBottom:2, flexShrink:0, maxWidth:"100%" }}>
        <div style={{ display:"inline-flex", flexDirection:"column", gap, minWidth:"min-content" }}>
          <div style={{ display:"flex", gap, paddingLeft:labelW + gap }}>
            {Array.from({ length:31 }, (_, i) => (
              <div key={i} className="tac-num" style={{ width:cell, textAlign:"center", fontSize:"0.5rem", lineHeight:"10px", color:t.textFaint, visibility: [1,5,10,15,20,25,31].includes(i + 1) ? "visible" : "hidden" }}>{i + 1}</div>
            ))}
          </div>
          {MONTHS_SHORT.map((mn, mi) => {
            const dim = new Date(year, mi + 1, 0).getDate();
            return (
              <div key={mn} style={{ display:"flex", gap, alignItems:"center" }}>
                <div style={{ width:labelW, flexShrink:0, fontSize:"0.6rem", color:t.textDim, fontWeight:600, textTransform:"lowercase", lineHeight:`${cell}px` }}>{mn}</div>
                {Array.from({ length:31 }, (_, di) => {
                  if (di + 1 > dim) return <div key={di} style={{ width:cell, height:cell }} />;
                  const d = new Date(year, mi, di + 1, 12);
                  const k = localDayKey(d);
                  const future = k > todayK;
                  const sec = byDay[k] || 0;
                  const clickable = !future;
                  return <div key={di} title={`${d.toLocaleDateString("pt-BR")}: ${sec ? fmt(sec) : "sem estudo"}${sec >= STREAK_MIN_SEC ? " ✓" : ""}`}
                    role={clickable ? "button" : undefined} tabIndex={clickable ? 0 : undefined}
                    aria-label={clickable ? `Resumo de ${d.toLocaleDateString("pt-BR")}` : undefined}
                    onClick={clickable ? () => setOpenDay(k) : undefined}
                    onKeyDown={clickable ? e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpenDay(k); } } : undefined}
                    style={{ width:cell, height:cell, borderRadius:3, background: future ? "transparent" : color(sec), cursor: clickable ? "pointer" : "default", flexShrink:0,
                      border: future ? `1px solid ${t.borderIn}` : k === todayK ? `1.5px solid ${t.ac}` : "none", boxSizing:"border-box",
                      ...(k === openDay ? { boxShadow:`0 0 0 2px ${t.bgCard}, 0 0 0 4px ${t.text}`, position:"relative", zIndex:2, transform:"scale(1.15)" } : null) }} />;
                })}
              </div>
            );
          })}
        </div>
      </div>

      </div>
      {openDay && <DaySummary dayKey={openDay} sessions={sessions} byDay={byDay} uid={uid} isAdmin={isAdmin} onClose={() => setOpenDay(null)} />}
    </div>
    <div className="g17-hover-lift" style={{ flex:"1 1 700px", minWidth:0, boxSizing:"border-box", background:t.bgCard, border:`1px solid ${t.border}`, borderRadius:12, padding: isMobile ? "0.85rem" : "0.9rem 1.1rem", position:"relative", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <TacCorners color={t.ac} size={10} thickness={1.5} inset={6} opacity={0.35} />
      <div style={{ display:"flex", flexWrap:"wrap", alignItems:"center", justifyContent:"center", gap:"1.2rem 2rem", width:"100%" }}>
      <div style={{ flex:"0 1 auto", minWidth:0, display:"flex", flexDirection:"column", gap:"0.5rem" }}>
        {pie.items.length === 0 ? (<>
          <span style={{ fontSize:"0.64rem", color:t.textDim, textTransform:"uppercase", letterSpacing:"0.08em", fontWeight:700 }}>Matérias estudadas · {year}</span>
          <div style={{ fontSize:"0.8rem", color:t.textFaint, padding:"0.8rem 0" }}>Nenhuma matéria lançada no Calendário em {year}.</div>
        </>) : (
          <div style={{ display:"flex", alignItems:"center", gap:"0.9rem", position:"relative" }}>
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:"0.5rem", flexShrink:0 }}>
            <span style={{ fontSize:"0.64rem", color:t.textDim, textTransform:"uppercase", letterSpacing:"0.08em", fontWeight:700, whiteSpace:"nowrap" }}>Matérias estudadas · {year}</span>
            <div style={{ position:"relative", width:PS, height:PS, flexShrink:0 }}
              onMouseMove={e => { const r = e.currentTarget.getBoundingClientRect(); setPieHover(h => h && { ...h, x: e.clientX - r.left, y: e.clientY - r.top }); }}
              onMouseLeave={() => setPieHover(null)}>
              <svg width={PS} height={PS} viewBox={`0 0 ${PS} ${PS}`} role="img" aria-label="Proporção de estudo por matéria" style={{ display:"block", overflow:"visible" }}>
                {pie.items.map((x, i) => (
                  <path key={x.name} d={arcPath(x.a0, x.a1, pieHover && pieHover.i === i)} fill={x.color}
                    stroke={t.bgCard} strokeWidth="1.5" strokeLinejoin="round"
                    style={{ cursor:"pointer", opacity: pieHover && pieHover.i !== i ? 0.55 : 1, transition:"opacity .12s" }}
                    onMouseEnter={e => { const r = e.currentTarget.ownerSVGElement.getBoundingClientRect(); setPieHover({ i, x: e.clientX - r.left, y: e.clientY - r.top }); }}
                    onClick={() => setPieHover(h => h && h.i === i ? null : { i, x: PS / 2, y: PS / 2 })} />
                ))}
              </svg>
              <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", pointerEvents:"none" }}>
                <span className="tac-num" style={{ fontSize:"0.95rem", fontWeight:800, color:t.text, lineHeight:1.1 }}>{fmtH(pie.total)}</span>
                <span style={{ fontSize:"0.54rem", color:t.textFaint, marginTop:2 }}>total</span>
              </div>
              {pieHover && pie.items[pieHover.i] && (
                <div style={{ position:"absolute", left:Math.min(pieHover.x + 12, PS + 20), top:Math.max(pieHover.y - 44, -40), zIndex:20, pointerEvents:"none", background:t.bgCard, border:`1px solid ${t.border}`, borderRadius:8, padding:"0.4rem 0.6rem", boxShadow:"0 6px 18px rgba(0,0,0,0.4)", whiteSpace:"nowrap" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:"0.4rem", fontSize:"0.76rem", fontWeight:700, color:t.text }}>
                    <span style={{ width:8, height:8, borderRadius:2, background:pie.items[pieHover.i].color }} />{pie.items[pieHover.i].name}
                  </div>
                  <div className="tac-num" style={{ fontSize:"0.72rem", color:t.textMid, marginTop:2 }}>{fmtH(pie.items[pieHover.i].sec)} · {Math.round(pie.items[pieHover.i].pct * 100)}%</div>
                </div>
              )}
            </div>
            </div>
            <div style={{ flex:"0 1 150px", minWidth:0, display:"flex", flexDirection:"column", gap:"0.28rem" }}>
              {pie.items.slice(0, isMobile ? 5 : 6).map((x, i) => (
                <div key={x.name} onMouseEnter={() => setPieHover({ i, x: PS / 2, y: PS / 2 })} onMouseLeave={() => setPieHover(null)}
                  style={{ display:"flex", alignItems:"center", gap:"0.4rem", fontSize:"0.7rem", color: pieHover && pieHover.i === i ? t.text : t.textMid, minWidth:0 }}>
                  <span style={{ width:8, height:8, borderRadius:2, background:x.color, flexShrink:0 }} />
                  <span style={{ flex:1, minWidth:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{x.name}</span>
                  <span className="tac-num" style={{ color:t.textDim, flexShrink:0 }}>{Math.round(x.pct * 100)}%</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
        <RhythmCard bare byDay={byDay} sessions={sessions} loaded={loaded} />
      </div>
    </div>
    </div>
  );
}

// ── "Seu ritmo": melhor dia da semana e horário em que mais registra estudo ─────
function RhythmCard({ byDay, sessions, loaded, bare }) {
  const t = useTheme();
  const [sel, setSel] = useState(null);          // dia da semana clicado
  const [showInfo, setShowInfo] = useState(false); // detalhes (melhor dia, horário forte…)
  const WD = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
  const WD_FULL = ["domingo", "segunda", "terça", "quarta", "quinta", "sexta", "sábado"];
  const fmtM = sc => { const m = Math.round(sc / 60); return m >= 60 ? `${Math.floor(m / 60)}h${String(m % 60).padStart(2, "0")}` : `${m} min`; };
  const d = (() => {
    // Semana atual (domingo a sábado): cada ponto é o tempo estudado naquele dia.
    const today = new Date(); today.setHours(12, 0, 0, 0);
    const todayIdx = today.getDay();
    const start = new Date(today); start.setDate(start.getDate() - todayIdx);
    const avg = Array(7).fill(0), keys = [];
    let total = 0, daysOk = 0;
    for (let i = 0; i <= todayIdx; i++) {
      const dd = new Date(start); dd.setDate(start.getDate() + i);
      const k = localDayKey(dd); keys.push(k);
      avg[i] = byDay[k] || 0; total += avg[i];
      if (avg[i] >= STREAK_MIN_SEC) daysOk++;
    }
    const maxAvg = Math.max(...avg);
    const best = maxAvg > 0 ? avg.indexOf(maxAvg) : -1;
    const hours = Array(24).fill(0);
    const weekKeys = new Set(keys);
    Object.entries(sessions).forEach(([k, list]) => { if (weekKeys.has(k)) list.forEach(x => { if (x.sec >= 300) hours[new Date(x.at).getHours()] += x.sec; }); });
    const hMax = Math.max(...hours);
    const peak = hMax > 0 ? hours.indexOf(hMax) : -1;
    return { avg, maxAvg, best, peak, todayIdx, total, daysOk };
  })();
  const H = 58;
  return (
    <div className={bare ? undefined : "g17-hover-lift"} style={bare
      ? { flex:"0 1 300px", minWidth:220, display:"flex", flexDirection:"column", justifyContent:"center", alignItems:"center", gap:"0.8rem" }
      : { flex:"1 1 250px", minWidth:240, boxSizing:"border-box", background:t.bgCard, border:`1px solid ${t.border}`, borderRadius:12, padding:"0.9rem 1.1rem", position:"relative", display:"flex", flexDirection:"column", justifyContent:"center", alignItems:"center", gap:"0.8rem" }}>
      {!bare && <TacCorners color={t.ac} size={10} thickness={1.5} inset={6} opacity={0.35} />}
      <span style={{ fontSize:"0.64rem", color:t.textDim, textTransform:"uppercase", letterSpacing:"0.08em", fontWeight:700, textAlign:"center" }}>Seu ritmo · esta semana</span>
      {!loaded ? <div style={{ fontSize:"0.8rem", color:t.textFaint }}>Carregando…</div> : d.best < 0 ? (
        <div style={{ fontSize:"0.8rem", color:t.textFaint, lineHeight:1.5 }}>Ainda sem estudo nesta semana. Inicie o cronômetro para ver seu ritmo.</div>
      ) : (<div style={{ display:"flex", flexWrap:"wrap", gap:"0.9rem 1.6rem", alignItems:"center", justifyContent:"center", width:"100%" }}>
        <div style={{ flex:"0 1 300px", minWidth:200, maxWidth:360 }}>
          {(() => {
            const W = 300, pad = 6;
            const pts = d.avg.map((v, i) => [pad + i * ((W - 2 * pad) / 6), H - 4 - (d.maxAvg ? (v / d.maxAvg) * (H - 10) : 0)]).slice(0, d.todayIdx + 1);
            const line = pts.map((p, i) => `${i ? "L" : "M"} ${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(" ");
            const area = `${line} L ${pts[pts.length - 1][0].toFixed(1)} ${H} L ${pts[0][0].toFixed(1)} ${H} Z`;
            return (
              <div style={{ position:"relative", height:H }}>
                <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ display:"block", overflow:"visible" }} aria-label="Estudo por dia da semana atual">
                  <defs>
                    <linearGradient id="rhythmFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={t.ac} stopOpacity="0.35" />
                      <stop offset="100%" stopColor={t.ac} stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  {[0.33, 0.66].map(g => <line key={g} x1="0" x2={W} y1={H * g} y2={H * g} stroke={t.borderIn} strokeDasharray="3 4" vectorEffect="non-scaling-stroke" />)}
                  <path d={area} fill="url(#rhythmFill)" />
                  <path d={line} fill="none" stroke={t.ac} strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
                </svg>
                {pts.map((p, i) => (
                  <div key={i} role="button" tabIndex={0} aria-label={`${WD_FULL[i]}: ${fmtM(d.avg[i])}`} onClick={() => setSel(v => v === i ? null : i)}
                    onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSel(v => v === i ? null : i); } }}
                    style={{ position:"absolute", left:`${(p[0] / W) * 100}%`, top:p[1], width:22, height:22, transform:"translate(-50%, -50%)", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer" }}>
                    <div style={{ width: sel === i || i === d.best ? 11 : 7, height: sel === i || i === d.best ? 11 : 7, borderRadius:"50%", background: sel === i || i === d.best ? t.ac : t.bgCard, border:`2px solid ${t.ac}`,
                      boxShadow: sel === i ? `0 0 0 4px ${t.acDim}` : "none", transition:"all .12s", boxSizing:"border-box" }} />
                  </div>
                ))}
              </div>
            );
          })()}
          <div style={{ display:"flex", justifyContent:"space-between", marginTop:6, padding:"0 1px" }}>
            {WD.map((w, i) => <span key={i} onClick={() => setSel(v => v === i ? null : i)} style={{ width:22, textAlign:"center", fontSize:"0.58rem", cursor:"pointer", fontWeight: sel === i ? 800 : 500, color: sel === i ? t.ac : t.textFaint }}>{w}</span>)}
          </div>
          <div style={{ minHeight:22, marginTop:8, textAlign:"center", fontSize:"0.74rem", color:t.textMid }}>
            {sel !== null
              ? <><strong style={{ color:t.text, textTransform:"capitalize" }}>{WD_FULL[sel]}</strong> · <strong className="tac-num" style={{ color:t.text }}>{fmtM(d.avg[sel])}</strong>{sel === d.best && <span style={{ color:t.ac, fontWeight:700 }}> · seu melhor dia</span>}</>
              : <span style={{ color:t.textFaint, fontSize:"0.68rem" }}>Toque em um dia para ver o tempo</span>}
          </div>
          <div style={{ textAlign:"center" }}>
            <button onClick={() => setShowInfo(v => !v)} aria-expanded={showInfo}
              style={{ background:"transparent", border:"none", color:t.ac, fontSize:"0.72rem", cursor:"pointer", padding:"0.2rem 0", fontFamily:"inherit" }}>{showInfo ? "Ocultar detalhes" : "Ver detalhes"}</button>
          </div>
          {showInfo && (
            <div style={{ marginTop:6, display:"flex", flexDirection:"column", gap:"0.3rem", fontSize:"0.76rem", color:t.textMid, background:t.bgInner, border:`1px solid ${t.borderIn}`, borderRadius:10, padding:"0.6rem 0.8rem" }}>
              <div>Melhor dia: <strong style={{ color:t.text, textTransform:"capitalize" }}>{WD_FULL[d.best]}</strong> <span style={{ color:t.textDim }}>· {fmtM(d.avg[d.best])}</span></div>
              {d.peak >= 0 && <div>Horário forte: <strong className="tac-num" style={{ color:t.text }}>{String(d.peak).padStart(2, "0")}h às {String((d.peak + 1) % 24).padStart(2, "0")}h</strong></div>}
              <div>Na semana: <strong className="tac-num" style={{ color:t.text }}>{fmtM(d.total)}</strong> <span style={{ color:t.textDim }}>· {d.daysOk} {d.daysOk === 1 ? "dia válido" : "dias válidos"}</span></div>
            </div>
          )}
        </div>
      </div>)}
    </div>
  );
}

const DASH_DEFAULT_ORDER = ["frase", "resumo", "sequencia", "hoje", "questoes", "ranking"];

export function Dashboard({ routines, cycles, simSaved, projections, timerStudiedSec, publicName, questEntries, currentUserId, adminEmail }) {
  const t = useTheme();
  
  // State para controlar qual projeção está sendo exibida
  const [selectedProjIdx, setSelectedProjIdx] = React.useState(0);

  // Week hours: timer-studied sessions stored in cycles, scoped to current week (Sun–Sat)
  const now = new Date();
  const dayOfWeek = now.getDay(); // 0=Sun
  const weekStart = new Date(now); weekStart.setDate(now.getDate() - dayOfWeek); weekStart.setHours(0,0,0,0);
  const weekEnd   = new Date(weekStart); weekEnd.setDate(weekStart.getDate() + 6); weekEnd.setHours(23,59,59,999);

  // Sum studiedSec across all active/saved cycles for this week
  // We don't have per-session timestamps in cycles, so we use timerStudiedSec for "since start"
  // For "this week" we store weekly sessions in localStorage separately
  const weekSec = (() => {
    try {
      const sessions = JSON.parse(localStorage.getItem("sf_sessions") || "[]");
      const wk = sessions.filter(s => { const d = new Date(s.ts); return d >= weekStart && d <= weekEnd; });
      return wk.reduce((a,s) => a + s.sec, 0);
    } catch(e) { return 0; }
  })();
  const studyDays = useStudyDays(currentUserId, ADMIN_EMAILS.includes(adminEmail));
  const bdg = badgeExtra(studyDays, ADMIN_EMAILS.includes(adminEmail), timerStudiedSec);
  const weekSecDb = Object.entries(studyDays.byDay).reduce((a, [k, v]) => { const d = new Date(k + "T12:00:00"); return d >= weekStart && d <= weekEnd ? a + v : a; }, 0);
  const weekH   = (Math.max(weekSec, weekSecDb) / 3600).toFixed(1);
  const totalH  = ((timerStudiedSec||0) / 3600).toFixed(1);

  // Last simulado
  const lastSim = simSaved && simSaved.length > 0
    ? [...simSaved].sort((a,b) => new Date(b.date) - new Date(a.date))[0]
    : null;

  // Active cycle name
  const activeCycle = (() => { try { return JSON.parse(localStorage.getItem("sf_activeCycle")); } catch(e){ return null; } })();

  // Dismiss state — stored per date so it resets next day
  const todayKey = now.toISOString().slice(0,10);
  const [dismissed, setDismissed] = React.useState(() => {
    try { return localStorage.getItem("sf_dismiss") === todayKey; } catch(e){ return false; }
  });
  function dismissSuggestion() {
    setDismissed(true);
    try { localStorage.setItem("sf_dismiss", todayKey); } catch(e){}
  }

  // Today's suggestion: subjects from active cycle using the EXACT daily routine capacity
  const { todaySuggestions, dailyMinAvail } = (() => {
    if (!activeCycle) return { todaySuggestions: [], dailyMinAvail: 0 };
    const todayIdx = now.getDay();
    let cap = 0;
    if (routines && routines.length > 0) {
      routines[0].slots && routines[0].slots.forEach(s => {
        if (s.isStudy && s.days && s.days.includes(todayIdx) && s.start && s.end) {
          const toMin = t => t.split(":").reduce((a,v,i)=>a+(i===0?Number(v)*60:Number(v)),0);
          cap += Math.max(0, toMin(s.end) - toMin(s.start));
        }
      });
    }
    if (cap === 0) cap = 120; // fallback
    // Group by subject name to avoid same subject appearing multiple times
    const grouped = {};
    (activeCycle.subjects || []).forEach(s => {
      const rem = Math.max(0, (s.blockMin||0) - Math.floor((s.studiedSec||0)/60));
      if (rem <= 0) return;
      const key = s.name || "?";
      if (!grouped[key]) grouped[key] = { ...s, name:key, remaining:0, weight:s.weight||1, stars:s.stars||0 };
      grouped[key].remaining += rem;
    });
    // Sort by priority: more weight × less experience = more urgent
    const pending = Object.values(grouped)
      .sort((a,b) => (b.weight*(5-b.stars)) - (a.weight*(5-a.stars)));
    // Diversify: distribute budget round-robin so max 2 picks per subject, min 30min each
    const picked = []; let budget = cap;
    const passLimit = 2;
    for (let pass = 0; pass < passLimit && budget >= 30; pass++) {
      for (const s of pending) {
        if (budget < 30) break;
        const alreadyMin = picked.filter(p=>p.name===s.name).reduce((a,p)=>a+p.allocMin,0);
        if (alreadyMin >= s.remaining) continue; // fully covered
        const maxAlloc = Math.min(s.remaining - alreadyMin, pass===0 ? Math.min(120,budget) : budget);
        if (maxAlloc < 30) continue;
        const mins = Math.min(maxAlloc, budget);
        if (mins >= 30) { picked.push({ ...s, allocMin: mins }); budget -= mins; }
      }
    }
    return { todaySuggestions: picked, dailyMinAvail: cap };
  })();

  // Progress: how much of suggested blocks has been studied today
  const suggestionProgress = (() => {
    const totalAlloc = todaySuggestions.reduce((a,s)=>a+s.allocMin,0);
    if (totalAlloc === 0) return 0;
    // Sum studiedSec for suggested subjects from today's sessions
    const todaySessions = (() => { try { return JSON.parse(localStorage.getItem("sf_sessions")||"[]"); } catch(e){ return []; } })();
    const todayStudied  = todaySessions
      .filter(s => s.ts && new Date(s.ts).toISOString().slice(0,10) === todayKey)
      .reduce((a,s)=>a+s.sec,0);
    return Math.min(100, Math.round((todayStudied/60/totalAlloc)*100));
  })();

  // First projection for dashboard bar chart - usa o índice selecionado
  const firstProj = projections && projections.length > 0 ? projections[selectedProjIdx] || projections[0] : null;

  const dateStr = now.toLocaleDateString("pt-BR",{weekday:"long",day:"numeric",month:"long"});

  // Frase do dia — determinística pelo número do dia do ano
  const dayOfYear = Math.floor((now - new Date(now.getFullYear(),0,0)) / 86400000);
  const dailyPhrase = FRASES_DO_DIA[dayOfYear % FRASES_DO_DIA.length];

  const dashBlocks = {
    frase: (
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1.2fr", gap:"1rem", marginBottom:0 }}>
        <div className="g17-hover-lift" style={{ background:`linear-gradient(135deg, ${t.bgCard} 0%, ${t.acDim} 100%)`, border:`1px solid ${t.ac}40`, borderRadius:12, padding:"1.2rem", position:"relative", overflow:"hidden" }}>
          <TacCorners color={t.ac} size={11} thickness={1.5} inset={7} opacity={0.45} zIndex={2} />
          <style>{`
            @keyframes gradMove {
              0%   { background-position: 0% 50%; }
              50%  { background-position: 100% 50%; }
              100% { background-position: 0% 50%; }
            }
            @keyframes floatOrb1 {
              0%, 100% { transform: translate(0,0) scale(1); }
              50%       { transform: translate(-12px, 8px) scale(1.1); }
            }
            @keyframes floatOrb2 {
              0%, 100% { transform: translate(0,0) scale(1); }
              50%       { transform: translate(10px, -10px) scale(0.9); }
            }
            @keyframes floatOrb3 {
              0%, 100% { transform: translate(0,0); }
              33%       { transform: translate(6px, 10px); }
              66%       { transform: translate(-8px, 4px); }
            }
            .frase-bg {
              position: absolute; inset: 0;
              background: linear-gradient(135deg, ${t.bgCard} 0%, ${t.acDim} 50%, ${t.ac}18 100%);
              background-size: 200% 200%;
              animation: gradMove 8s ease infinite;
            }
          `}</style>
          <div className="frase-bg"/>
          <div style={{ position:"absolute", top:-20, right:-20, width:100, height:100, background:t.ac, opacity:0.07, borderRadius:"50%", animation:"floatOrb1 7s ease-in-out infinite" }}/>
          <div style={{ position:"absolute", bottom:-15, left:10, width:60, height:60, background:t.ac, opacity:0.05, borderRadius:"50%", animation:"floatOrb2 9s ease-in-out infinite" }}/>
          <div style={{ position:"absolute", top:"40%", right:"20%", width:35, height:35, background:t.ac, opacity:0.06, borderRadius:"50%", animation:"floatOrb3 11s ease-in-out infinite" }}/>
          <div style={{ position:"relative", zIndex:1 }}>
            <p style={{ margin:"0 0 0.5rem", fontSize:"0.65rem", textTransform:"uppercase", letterSpacing:"0.15em", color:t.ac, fontWeight:700 }}>✦ Frase do dia</p>
            <p style={{ margin:"0 0 0.8rem", fontSize:"0.92rem", color:t.text, fontStyle:"italic", lineHeight:1.5, fontWeight:500 }}>"{dailyPhrase}"</p>
          </div>
        </div>
        <div className="g17-hover-lift" style={{ background:t.bgCard, border:`1px solid ${t.border}`, borderRadius:12, padding:"1.2rem", position:"relative" }}>
          <TacCorners color={t.ac} size={11} thickness={1.5} inset={7} opacity={0.35} />
          <BadgesGrid totalSec={bdg.totalSec} simSaved={simSaved} questEntries={questEntries} extra={bdg.extra} uid={currentUserId} compact={true} loading={!studyDays.loaded} />
        </div>
      </div>
    ),
    resumo: (
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "1rem" }}>
        {[
          { label: "Horas esta semana", value: weekH+"h",     delta: `Total: ${totalH}h desde o início`, color: t.ac },
          { label: "Total desde início",value: totalH+"h",    delta: "Apenas timer",                    color: "#fbbf24" },
          { label: "Último simulado",   value: lastSim ? lastSim.net+"pts" : "—", delta: lastSim ? lastSim.name : "Nenhum ainda", color: "#60a5fa" },
          { label: "Ciclo atual",       value: activeCycle ? activeCycle.name : "—", delta: activeCycle ? (()=>{ const names = [...new Set((activeCycle.subjects||[]).map(s=>s.name))]; const n=names.length; return n+` matéria${n===1?"":"s"}`; })() : "Crie um ciclo", color: "#f472b6" },
        ].map((s,si) => (
          <div key={s.label} className="g17-hover-lift" style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 12, padding: "1.1rem", position:"relative" }}>
            <TacCorners color={s.color} size={9} thickness={1.5} inset={6} opacity={0.4} />
            <div style={{ display:"flex", alignItems:"center", gap:"0.35rem", marginBottom:"0.5rem" }}>
              <span className="g17-mono" style={{ fontSize:"0.6rem", color:s.color, opacity:0.7 }}>0{si+1}</span>
              <p style={{ color: t.textDim, fontSize: "0.68rem", margin: 0, textTransform: "uppercase", letterSpacing: "0.1em", fontWeight:600 }}>{s.label}</p>
            </div>
            <p className="tac-num" style={{ fontSize: "1.55rem", fontWeight: 800, margin: 0, color: s.color, letterSpacing: "-0.01em" }}>{s.value}</p>
            <p style={{ color: t.textFaint, fontSize: "0.72rem", margin: "0.3rem 0 0" }}>{s.delta}</p>
          </div>
        ))}
      </div>
    ),
    sequencia: (
      <StudyStreak byDay={studyDays.byDay} sessions={studyDays.sessions} cal={studyDays.cal} loaded={studyDays.loaded} uid={currentUserId} isAdmin={ADMIN_EMAILS.includes(adminEmail)} />
    ),
    hoje: (
      <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: "1rem" }}>
        <div className="g17-hover-lift" style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 12, padding: "1.2rem", position:"relative" }}>
          <TacCorners color={t.ac} size={11} thickness={1.5} inset={7} opacity={0.35} />
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"0.75rem" }}>
            <TacEyebrow color={t.textDim}>
              Sugestão de hoje
              {dailyMinAvail > 0 && <span style={{ color:t.textGhost, fontWeight:400, textTransform:"none", letterSpacing:"normal", marginLeft:"0.3rem" }}>· {Math.floor(dailyMinAvail/60)}h{dailyMinAvail%60>0?dailyMinAvail%60+"min":""} disponíveis</span>}
            </TacEyebrow>
            {!dismissed && todaySuggestions.length > 0 && (
              <button onClick={dismissSuggestion} style={{ background:"transparent", border:`1px solid ${t.border}`, borderRadius:6, padding:"0.2rem 0.6rem", fontSize:"0.68rem", color:t.textGhost, cursor:"pointer" }}>Dispensar</button>
            )}
          </div>

          {dismissed ? (
            <div style={{ textAlign:"center", padding:"1rem 0" }}>
              <p style={{ color:t.textFaint, fontSize:"0.83rem", margin:"0 0 0.4rem" }}>Sugestão dispensada por hoje. 👋</p>
              <button onClick={()=>{ setDismissed(false); try { localStorage.removeItem("sf_dismiss"); } catch(e){} }}
                style={{ background:"transparent", border:`1px solid ${t.border}`, borderRadius:6, padding:"0.25rem 0.7rem", fontSize:"0.72rem", color:t.textDim, cursor:"pointer" }}>Ver sugestão</button>
            </div>
          ) : todaySuggestions.length === 0 ? (
            <p style={{ color: t.textFaint, fontSize: "0.83rem", margin: 0 }}>
              {activeCycle ? "🎉 Todas as matérias do ciclo estão em dia!" : "Crie um ciclo de estudos para ver sugestões aqui."}
            </p>
          ) : (
            <>
              {/* Progress bar */}
              {suggestionProgress > 0 && (
                <div style={{ marginBottom:"0.9rem" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:"0.35rem" }}>
                    <span className="g17-mono" style={{ fontSize:"0.64rem", letterSpacing:"0.06em", color:t.textDim, textTransform:"uppercase" }}>Progresso de hoje</span>
                    <span className="tac-num" style={{ fontSize:"0.68rem", fontWeight:700, color:suggestionProgress>=100?t.ac:t.textMid }}>{suggestionProgress}%</span>
                  </div>
                  <TickBar value={suggestionProgress} segments={28} color={t.ac} height={6} />
                </div>
              )}
              {todaySuggestions.map((s, i) => {
                const donePct = s.blockMin > 0 ? Math.min(100, Math.round(((s.studiedSec||0)/60/s.blockMin)*100)) : 0;
                return (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: "1rem", padding: "0.65rem 0", borderBottom: i < todaySuggestions.length-1 ? `1px solid ${t.borderIn}` : "none" }}>
                  <div style={{ width: 3, height: 36, background: s.color||t.ac, borderRadius: 99, flexShrink: 0 }} />
                  <div style={{ flex:1, minWidth:0 }}>
                    <p style={{ margin: 0, fontSize: "0.85rem", fontWeight: 600, color: t.textMid }}>{s.name}</p>
                    <div style={{ display:"flex", alignItems:"center", gap:"0.4rem", marginTop:"0.2rem" }}>
                      <div style={{ flex:1, height:3, background:t.bgInner, borderRadius:99, overflow:"hidden" }}>
                        <div style={{ height:"100%", width:`${donePct}%`, background:s.color||t.ac, borderRadius:99 }}/>
                      </div>
                      <span style={{ fontSize:"0.62rem", color:t.textFaint, flexShrink:0 }}>{donePct}%</span>
                    </div>
                  </div>
                  <div style={{ marginLeft: "auto", background: (s.color||t.ac)+"18", border: `1px solid ${(s.color||t.ac)}30`, borderRadius: 6, padding: "0.2rem 0.6rem", fontSize: "0.7rem", color: s.color||t.ac, flexShrink:0 }}>{s.allocMin}min</div>
                </div>
              )})}
            </>
          )}
        </div>
        <div className="g17-hover-lift" style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 12, padding: "1.2rem", position:"relative" }}>
          <TacCorners color={t.ac} size={11} thickness={1.5} inset={7} opacity={0.35} />
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"1rem" }}>
            <TacEyebrow color={t.textDim}>Projeção Simulado</TacEyebrow>
            {projections && projections.length > 1 && (
              <select 
                value={selectedProjIdx} 
                onChange={(e)=>{
                  setSelectedProjIdx(parseInt(e.target.value));
                }}
                style={{ background:t.bgInner, border:`1px solid ${t.border}`, borderRadius:6, padding:"0.2rem 0.5rem", color:t.text, fontSize:"0.7rem", cursor:"pointer", outline:"none" }}>
                {projections.map((proj,i)=>(
                  <option key={i} value={i}>{proj.name}</option>
                ))}
              </select>
            )}
          </div>
          {!firstProj ? (
            <p style={{ color: t.textFaint, fontSize: "0.83rem", margin: 0 }}>Crie uma projeção na aba Simulados.</p>
          ) : (() => {
            const steps = firstProj.steps || [];
            const maxNeed = Math.max(...steps.map(s => s.need||0), Number(firstProj.target)||1);
            
            // Calcular nota atual: pegar o último simulado feito
            const doneSteps = steps.filter(s => s.done && s.actual != null);
            const lastDone = doneSteps.length > 0 ? doneSteps[doneSteps.length - 1] : null;
            const currentActual = lastDone ? lastDone.actual : (Number(firstProj.currentScore) || 0);
            
            return (
              <>
                <div style={{ 
                  display: "flex", 
                  alignItems: "flex-end", 
                  gap: 4, 
                  minHeight: 85,
                  maxHeight: 110,
                  maxWidth: "calc(16 * 46px + 23px)", // 16 blocos completos (42px + 4px gap) + metade do 17º
                  overflowX: "auto", 
                  overflowY: "visible",
                  paddingTop: "0.5rem", 
                  paddingBottom: "0.5rem",
                  scrollbarWidth: "thin",
                  scrollbarColor: `${t.ac}40 ${t.bgInner}`
                }}>
                  {steps.map((s, i) => {
                    const isDone = s.done && s.actual !== null;
                    const needScore = s.need || 0;
                    const actualScore = s.actual || 0;
                    
                    // Verificar redação - SEMPRE que tiver no step
                    const hasRedacao = (s.redacao != null && s.redacao !== undefined && s.redacao > 0);
                    
                    // Calcular altura PROPORCIONAL à meta (tipo escada)
                    const proportionalHeight = (needScore/maxNeed)*88;
                    // Limitar apenas os MUITO ALTOS (acima de 70px) para não estourar
                    const maxAllowedHeight = 70;
                    const finalHeight = Math.min(maxAllowedHeight, Math.max(38, proportionalHeight));
                    
                    // Calcular porcentagem e cor
                    let fillColor = "#64748b";
                    
                    if (isDone) {
                      if (needScore > 0) {
                        // Tem meta definida - calcular porcentagem
                        const percentage = (actualScore / needScore) * 100;
                        if (percentage >= 100) fillColor = "#4ade80";
                        else if (percentage >= 70) fillColor = "#fbbf24";
                        else fillColor = "#f87171";
                      } else {
                        // SEM meta (registrado antes da projeção ou manualmente) - sempre verde
                        fillColor = "#4ade80";
                      }
                    } else if (!isDone) {
                      const today = new Date();
                      const stepDate = new Date(s.date + "T12:00");
                      const daysUntil = Math.floor((stepDate - today) / (1000*60*60*24));
                      
                      if (daysUntil < 0) fillColor = "#991b1b";
                      else if (daysUntil === 0) fillColor = "#fb923c";
                      else if (daysUntil <= 2) fillColor = "#fbbf24";
                      else fillColor = "#64748b";
                    }
                    
                    // Altura do bloco objetiva
                    const objHeight = hasRedacao ? 22 : finalHeight;
                    
                    return (
                      <div key={i} style={{ flex: "0 0 auto", minWidth: 42, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                        {/* Bloco objetiva */}
                        <div style={{ 
                          width: 42,
                          height: objHeight,
                          borderRadius: hasRedacao ? "6px 6px 0 0" : 8,
                          background: fillColor + "15",
                          border: `1px solid ${fillColor}40`,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          color: fillColor
                        }}>
                          <span style={{ fontSize: "0.75rem", fontWeight: 900, lineHeight: 1 }}>
                            {isDone ? actualScore : needScore}
                          </span>
                          {!hasRedacao && (
                            <span style={{ fontSize: "0.48rem", color: t.textFaint, lineHeight: 1 }}>
                              {isDone ? "obj" : "meta"}
                            </span>
                          )}
                        </div>
                        
                        {/* Bloco redação - mostra se tiver */}
                        {hasRedacao && (
                          <div style={{ 
                            width: 42,
                            height: 22,
                            borderRadius: "0 0 6px 6px",
                            background: "#a78bfa15",
                            border: "1px solid #a78bfa40",
                            borderTop: "none",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            marginTop: "-1px"
                          }}>
                            <span style={{ fontSize: "0.75rem", fontWeight: 900, color: "#a78bfa", lineHeight: 1 }}>
                              {s.redacao}
                            </span>
                            <span style={{ fontSize: "0.48rem", color: "#a78bfa80", lineHeight: 1 }}>red</span>
                          </div>
                        )}
                        
                        {/* Data embaixo */}
                        <span style={{ fontSize: "0.55rem", color: t.textGhost, whiteSpace: "nowrap" }}>
                          {new Date(s.date+"T12:00").toLocaleDateString("pt-BR",{day:"2-digit",month:"2-digit"})}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: "0.6rem" }}>
                  <span style={{ fontSize: "0.7rem", color: t.textDim }}>Atual: <strong style={{ color: t.ac }}>{currentActual} pts</strong></span>
                  <span style={{ fontSize: "0.7rem", color: t.textDim }}>Meta: <strong style={{ color: "#fbbf24" }}>{firstProj.target} pts</strong></span>
                </div>
              </>
            );
          })()}
        </div>
      </div>
    ),
    questoes: (
      questEntries && questEntries.length > 0 && (() => {
        const last7 = {};
        const today2 = new Date(); today2.setHours(0,0,0,0);
        for (let d=6;d>=0;d--) { const x=new Date(today2); x.setDate(x.getDate()-d); last7[x.toISOString().slice(0,10)]={total:0,right:0,wrong:0}; }
        questEntries.forEach(e => { if (last7[e.date]) { last7[e.date].total+=e.total; last7[e.date].right+=e.right; last7[e.date].wrong+=(e.wrong||0); } });
        const days7 = Object.entries(last7).map(([date,v])=>({date,...v}));
        const maxQ  = Math.max(...days7.map(d=>d.total),1);
        const w7R   = days7.reduce((a,d)=>a+d.right,0);
        const w7W   = days7.reduce((a,d)=>a+d.wrong,0);
        const w7T   = days7.reduce((a,d)=>a+d.total,0);
        const w7Pct = w7T>0?Math.round((w7R/w7T)*100):0;
        const DN    = ["Dom","Seg","Ter","Qua","Qui","Sex","Sáb"];
        return (
          <div className="g17-hover-lift" style={{ background:t.bgCard, border:`1px solid ${t.border}`, borderRadius:12, padding:"1.2rem", position:"relative" }}>
            <TacCorners color={t.ac} size={11} thickness={1.5} inset={7} opacity={0.35} />
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"1rem" }}>
              <TacEyebrow color={t.textDim}>Questões — últimos 7 dias</TacEyebrow>
              <div style={{ display:"flex", gap:"1.2rem", textAlign:"right" }}>
                <div>
                  <p style={{ margin:0, fontSize:"1.2rem", fontWeight:800, color:t.ac }}>{w7T}</p>
                  <p style={{ margin:0, fontSize:"0.62rem", color:t.textDim }}>questões</p>
                </div>
                <div>
                  <p style={{ margin:0, fontSize:"1.2rem", fontWeight:800, color:"#4ade80" }}>{w7R} <span style={{fontSize:"0.75rem"}}>({w7Pct}%)</span></p>
                  <p style={{ margin:0, fontSize:"0.62rem", color:t.textDim }}>acertos</p>
                </div>
                <div>
                  <p style={{ margin:0, fontSize:"1.2rem", fontWeight:800, color:"#f87171" }}>{w7W}</p>
                  <p style={{ margin:0, fontSize:"0.62rem", color:t.textDim }}>erros</p>
                </div>
              </div>
            </div>
            <div style={{ display:"flex", alignItems:"flex-end", gap:6, height:60 }}>
              {days7.map((d,i) => {
                const tH = Math.max(3,(d.total/maxQ)*56);
                const rH = d.total>0?(d.right/d.total)*tH:0;
                const wH = d.total>0?(d.wrong/d.total)*tH:0;
                return (
                  <div key={d.date} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:3 }}>
                    <div style={{ width:"100%", height:Math.max(tH,3)+"px", background:t.bgInner, borderRadius:"3px 3px 0 0", position:"relative", overflow:"hidden" }}>
                      {rH>0 && <div style={{ position:"absolute", bottom:0, left:0, right:0, height:rH+"px", background:"#4ade80", opacity:0.85 }}/>}
                      {wH>0 && <div style={{ position:"absolute", bottom:rH+"px", left:0, right:0, height:wH+"px", background:"#f87171", opacity:0.75 }}/>}
                    </div>
                    <span style={{ fontSize:"0.62rem", color:t.textGhost }}>{DN[new Date(d.date+"T12:00").getDay()]}</span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()
    ),
    ranking: (
      <MiniRanking currentUserId={currentUserId} adminEmail={adminEmail} />
    ),
  };
  return (
    <div>
      <div style={{ marginBottom: "2rem", display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:"1rem", flexWrap:"wrap" }}>
       <div>
        <TacEyebrow color={t.ac}>{dateStr}</TacEyebrow>
        <h2 style={{ fontSize: "1.8rem", fontWeight: 800, margin: "0.5rem 0 0", color: t.text, letterSpacing: "-0.03em" }}>
          {now.getHours()<12?"Bom dia":now.getHours()<18?"Boa tarde":"Boa noite"}, <span style={{ color: t.ac }}>{publicName||"Estudante"}</span> 👋
        </h2>
       </div>
      </div>

      <div style={{ display:"flex", flexDirection:"column", gap:"1.5rem" }}>
        {DASH_DEFAULT_ORDER.map(id => dashBlocks[id] ? <div key={id}>{dashBlocks[id]}</div> : null)}
      </div>
    </div>
  );
}

// ── MINI RANKING (Dashboard) ──────────────────────────────────────────────────
function MiniRanking({ currentUserId, adminEmail }) {
  const t = useTheme();
  const mine = useMyRankOverride(currentUserId, ADMIN_EMAILS.includes(adminEmail));
  const [rows,       setRows]       = React.useState([]);
  const [dayMap,     setDayMap]     = React.useState({}); // { user_id: segundos hoje }
  const [period,     setPeriod]     = React.useState("semanal");
  const [loading,    setLoading]    = React.useState(true);
  const [expanded,   setExpanded]   = React.useState(false);
  const [viewProfile,setViewProfile]= React.useState(null);

  React.useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [{ data }, { data: today, error: todayError }] = await Promise.all([
          sb.from("ranking_view").select("*"),
          sb.from("today_ranking_view").select("id, today_seconds"),
        ]);
        if (todayError) console.error("Ranking diário (mini):", todayError);
        if (data) setRows(data);
        const map = {};
        (today || []).forEach(r => { map[r.id] = r.today_seconds || 0; });
        setDayMap(map);
      } catch {}
      setLoading(false);
    }
    load();
  }, []);

  const secOf = r => {
    const base = period === "semanal" ? r.week_seconds : period === "diario" ? (dayMap[r.id] || 0) : r.total_seconds;
    if (mine && r.id === currentUserId) return period === "semanal" ? mine.week : period === "diario" ? mine.day : mine.total;
    return base;
  };
  const secToH = s => (s/3600).toFixed(1);
  const sorted = [...rows].sort((a,b) => secOf(b) - secOf(a)).map((r,i) => ({ ...r, pos: i+1 }));

  const youIdx = sorted.findIndex(r => r.id === currentUserId);
  const top3   = sorted.slice(0, 3);
  const you    = youIdx >= 3 ? sorted[youIdx] : null; // só adiciona se não está no top3

  const displayRows = expanded ? sorted : [...top3, ...(you ? [you] : [])];

  function RankRow({ u, i, last }) {
    const sec   = secOf(u);
    const isYou = u.id === currentUserId;
    return (
      <div onClick={()=>setViewProfile(u.id)}
        style={{ display:"flex", alignItems:"center", gap:"0.8rem", padding:"0.65rem 1.2rem",
          borderBottom: last ? "none" : `1px solid ${t.borderIn}`,
          background: isYou ? t.acDim : "transparent", cursor:"pointer" }}>
        <span style={{ width:22, textAlign:"center", fontSize:u.pos<=3?"1rem":"0.75rem", fontWeight:700, color:u.pos<=3?"inherit":t.textDim, flexShrink:0 }}>
          {u.pos<=3 ? <MedalBadge pos={u.pos} /> : u.pos+"°"}
        </span>
        <div style={{ width:30, height:30, borderRadius:"50%", background:t.bgInner, border:`1.5px solid ${isYou?t.ac:t.border}`, overflow:"hidden", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, fontSize:"0.8rem" }}>
          {u.avatar_url ? <img src={u.avatar_url} style={{ width:"100%", height:"100%", objectFit:"cover" }} alt="" /> : <span style={{ color:t.textDim }}>{u.public_name?.[0]?.toUpperCase()||"?"}</span>}
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <p style={{ margin:0, fontSize:"0.85rem", color:isYou?t.ac:t.textMid, fontWeight:isYou?700:400, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
            {u.public_name}{isYou && <span style={{ fontSize:"0.65rem", color:t.textFaint, marginLeft:"0.3rem" }}>(você)</span>}
          </p>
          {u.target_contest && (() => {
            const known = findContest(u.target_contest);
            if (known) return <p style={{ margin:0, fontSize:"0.62rem", color:t.textFaint, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}><span style={{ display:"inline-flex", alignItems:"center", gap:"0.3rem", verticalAlign:"middle" }}><ContestEmblem id={known.id} size={15} />{known.label}</span></p>;
            const si = u.target_contest.indexOf(" ");
            const emoji = si>0?u.target_contest.slice(0,si):"⭐", name = si>0?u.target_contest.slice(si+1):u.target_contest;
            return <p style={{ margin:0, fontSize:"0.62rem", color:"#facc15", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{emoji} {name}</p>;
          })()}
        </div>
        <span style={{ fontSize:"0.82rem", fontWeight:700, color:isYou?t.ac:t.textDim, flexShrink:0 }}>{secToH(sec)}h</span>
      </div>
    );
  }

  return (
    <>
      {viewProfile && <UserProfileModal userId={viewProfile} currentUserId={currentUserId} onClose={()=>setViewProfile(null)} adminEmail={adminEmail} />}
      <div className="g17-hover-lift" style={{ background:t.bgCard, border:`1px solid ${t.border}`, borderRadius:12, overflow:"hidden" }}>
        <div style={{ padding:"0.9rem 1.2rem", borderBottom:`1px solid ${t.borderIn}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <h3 style={{ margin:0, fontSize:"0.78rem", textTransform:"uppercase", letterSpacing:"0.12em", color:t.textDim, display:"flex", alignItems:"center", gap:"0.5rem" }}><NavIcon id="trofeu" size={15} color={t.ac} />Ranking</h3>
          <div style={{ display:"flex", gap:"0.5rem", alignItems:"center" }}>
            <div style={{ display:"flex", gap:"0.3rem" }}>
              {[{id:"diario",label:"Hoje"},{id:"semanal",label:"Semana"},{id:"historico",label:"Total"}].map(p=>(
                <button key={p.id} onClick={()=>setPeriod(p.id)}
                  style={{ background:period===p.id?t.ac:"transparent", color:period===p.id?(t.mode==="dark"?"#060d1a":"#fff"):t.textDim, border:"none", borderRadius:6, padding:"0.22rem 0.65rem", fontSize:"0.7rem", fontWeight:period===p.id?700:400, cursor:"pointer" }}>
                  {p.label}
                </button>
              ))}
            </div>
            {sorted.length > 4 && (
              <button onClick={()=>setExpanded(v=>!v)}
                style={{ background:"transparent", border:`1px solid ${t.border}`, borderRadius:6, padding:"0.22rem 0.65rem", fontSize:"0.7rem", color:t.textDim, cursor:"pointer" }}>
                {expanded ? "▲ Recolher" : `▼ Ver todos`}
              </button>
            )}
          </div>
        </div>
        {loading ? (
          <div style={{ padding:"1.5rem", textAlign:"center" }}>
            <div style={{ width:24, height:24, border:`2px solid ${t.ac}`, borderTopColor:"transparent", borderRadius:"50%", animation:"spin 0.8s linear infinite", display:"inline-block" }}/>
          </div>
        ) : sorted.length === 0 ? (
          <p style={{ color:t.textFaint, textAlign:"center", padding:"1.5rem", margin:0, fontSize:"0.8rem" }}>Nenhum dado ainda.</p>
        ) : (
          <>
            {you && !expanded && (
              <div style={{ padding:"0.3rem 1.2rem", background:t.bgInner, borderBottom:`1px solid ${t.borderIn}` }}>
                <span style={{ fontSize:"0.62rem", color:t.textFaint }}>···</span>
              </div>
            )}
            {displayRows.map((u, i) => (
              <RankRow key={u.id} u={u} i={i} last={i===displayRows.length-1} />
            ))}
          </>
        )}
      </div>
    </>
  );
}
